import { createFileRoute, Link } from "@tanstack/react-router";
import { canonical, ROOFING_CONTRACTOR_ID } from "@/components/site/site-config";
import { PageHero, ServicePageBody } from "@/components/site/PageShell";
import { Breadcrumbs, breadcrumbListJsonLd } from "@/components/site/Breadcrumbs";
import { BUSINESS } from "@/components/site/constants";
import { Phone, CalendarCheck } from "lucide-react";

const FAQS: { q: string; a: string }[] = [
  {
    q: "Can shingles be installed on a flat roof?",
    a: "Generally no. Standard shingles are designed for roofs with enough pitch to shed water quickly. On a flat or low-slope surface, water can stand or move slowly enough to work up beneath the shingles, leading to leaks. Low-slope areas typically require a membrane system such as TPO or modified bitumen that is designed for slower drainage.",
  },
  {
    q: "What is TPO roofing?",
    a: "TPO is a single-ply reflective roofing membrane commonly used on low-slope roofs. Its seams are heat-welded together, and the light-colored surface reflects sunlight, which can help reduce heat buildup on Florida rooms, additions, and other low-slope areas.",
  },
  {
    q: "Can a leaking flat roof be repaired?",
    a: "Often, yes. A failed seam, flashing, or penetration can frequently be repaired when the surrounding membrane is still in serviceable condition. If the material is extensively deteriorated or leaks keep returning in different areas, replacement may be the more practical option.",
  },
  {
    q: "When should a flat roof be replaced?",
    a: "Replacement becomes more likely when the membrane shows widespread cracking, splitting, or deterioration, when leaks keep recurring after repairs, when drainage problems are significant, or when the system is simply near the end of its useful life. A roof inspection is the best way to determine where your roof stands.",
  },
  {
    q: "Is ponding water normal on a flat roof?",
    a: "Minor standing water that dries within roughly 48 hours after rain is not unusual on some low-slope roofs. Water that sits longer, returns in the same low areas, or leaves staining and debris marks is a sign of a drainage problem that should be evaluated, because prolonged water exposure accelerates membrane deterioration.",
  },
  {
    q: "Can you improve drainage on a flat roof?",
    a: "In many cases, yes. Depending on the roof design, drainage may be improved by addressing low areas, improving drainage points, or using tapered insulation to direct water toward drains or edges. The appropriate approach depends on the specific roof, and not every low-slope roof requires tapered insulation.",
  },
  {
    q: "Do you repair Florida room and lanai roofs?",
    a: "Yes. Florida rooms, lanais, screened porches, and additions commonly have lower slopes than the main house and often use membrane or low-slope roofing systems. Destination Roofing repairs and replaces these roofs throughout Citrus County.",
  },
  {
    q: "Do you install commercial flat roofing?",
    a: "Yes. Destination Roofing works on low-slope commercial roofing in addition to residential flat roofs. Larger commercial buildings are evaluated individually so the system, staging, and drainage plan fit the property.",
  },
];

function TrustRow() {
  const items = [
    "Free roof inspections and estimates",
    "Owner-performed inspections",
    "Local family-owned roofing contractor",
    "Residential and commercial roofing experience",
    "Repair and full replacement options",
    "Licensed and insured",
    `License #${" "}${BUSINESS.licenseNumber}`,
  ];
  return (
    <div className="not-prose bg-muted/50 border border-border rounded-lg p-4 my-6">
      <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
        {items.map((t) => (
          <li key={t} className="flex items-start gap-2">
            <span className="mt-1 size-1.5 rounded-full bg-cta shrink-0" aria-hidden />
            <span>{t}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function RepairVsReplace() {
  return (
    <div className="not-prose grid md:grid-cols-2 gap-4 my-6">
      <div className="border-2 border-cta rounded-lg p-5 bg-card">
        <h3 className="font-display font-bold text-lg mb-3">Repair may make sense when:</h3>
        <ul className="space-y-1.5 text-sm">
          <li>· The roofing system is still serviceable overall</li>
          <li>· The leak source can be isolated to a seam, flashing, or penetration</li>
          <li>· Damage is limited to one area</li>
          <li>· The membrane is not broadly deteriorated</li>
          <li>· The deck and substrate remain sound</li>
        </ul>
      </div>
      <div className="border border-border rounded-lg p-5 bg-card">
        <h3 className="font-display font-bold text-lg mb-3">Replacement may be more practical when:</h3>
        <ul className="space-y-1.5 text-sm">
          <li>· The membrane is extensively deteriorated</li>
          <li>· Leaks keep recurring after previous repairs</li>
          <li>· Drainage or ponding problems are significant</li>
          <li>· Seams and flashings are failing in multiple areas</li>
          <li>· The system is near the end of its useful life</li>
        </ul>
      </div>
    </div>
  );
}

export const Route = createFileRoute("/flat-roofing")({
  head: () => ({
    meta: [
      { title: "Flat & Low-Slope Roofing in Citrus County, FL | Destination Roofing" },
      { name: "description", content: "Flat and low-slope roof installation, replacement and repair in Citrus County, FL — TPO and modified bitumen for Florida rooms, lanais and additions. Free inspection. Call (352) 901-3094." },
      { name: "robots", content: "index,follow" },
      { property: "og:title", content: "Flat & Low-Slope Roofing in Citrus County, FL | Destination Roofing" },
      { property: "og:description", content: "TPO and modified-bitumen flat roofing installed, replaced and repaired in Citrus County. Free roof inspection and estimate." },
      { property: "og:url", content: canonical("/flat-roofing") },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: canonical("/flat-roofing") }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify([
        breadcrumbListJsonLd([{ label: "Services", href: "/services" }, { label: "Flat Roofing", href: "/flat-roofing" }]),
        {
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Flat & Low-Slope Roofing",
          serviceType: "Flat Roofing",
          description: "Flat and low-slope roof installation, replacement and repair — TPO and modified bitumen systems for Florida rooms, lanais, additions and commercial buildings in Citrus County, FL.",
          url: canonical("/flat-roofing"),
          provider: { "@id": ROOFING_CONTRACTOR_ID },
          areaServed: { "@type": "AdministrativeArea", name: "Citrus County, Florida" },
        },
        {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
      ]),
    }],
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <Breadcrumbs items={[{ label: "Services", href: "/services" }, { label: "Flat Roofing", href: "/flat-roofing" }]} />
      <PageHero showRating
        eyebrow="Flat Roofing"
        title="Flat & Low-Slope Roofing in Citrus County, FL"
        subtitle="Destination Roofing Inc. installs, replaces, and repairs flat and low-slope roofing systems on Florida rooms, lanais, additions, porches, and other low-slope areas — the parts of your home where standard shingles simply don't belong."
      />
      <ServicePageBody title="Flat Roofing" service="Flat Roofing">
        <TrustRow />

        <h2>Flat Roof Repair &amp; Replacement in Citrus County</h2>
        <p>
          Flat and low-slope roofs live a harder life than pitched roofs. Because water drains slowly — and sometimes not at all — these roofs need systems specifically designed for slower drainage and prolonged water exposure. A roofing material that performs perfectly on a steep main roof can fail quickly on a low-slope surface.
        </p>
        <p>
          Homeowners across Citrus County call us about flat roofs when they notice <Link to="/roof-repair">roof leaks</Link> over a Florida room or addition, an aging membrane that's cracking or blistering, seams and flashings that have started to fail, ponding water that never seems to dry, or roofing materials that have simply deteriorated past the point of repair. Any of these is a good reason to schedule a <Link to="/roof-inspection">free roof inspection</Link> before a small problem becomes interior damage.
        </p>

        <h2>Flat Roofing Systems We Install</h2>
        <p>
          We install low-slope roofing systems that are actually designed to handle standing water and Florida rain — not materials intended for pitched roofs.
        </p>
        <ul>
          <li><strong>TPO</strong> — a reflective single-ply roofing membrane commonly used on low-slope roofs, with heat-welded seams and good resistance to Florida heat.</li>
          <li><strong>Modified bitumen</strong> — a durable multi-layer rolled roofing option that suits certain residential low-slope applications.</li>
          <li><strong>Full tie-in</strong> to your main roof and existing flashings so water can't sneak in where the two systems meet.</li>
        </ul>
        <p>
          There is no single flat-roofing system that is always best. The appropriate system depends on the roof's design, drainage, current condition, and how the space below is used. During your inspection we'll explain which system fits your roof and why.
        </p>

        <h2>TPO Roofing in Citrus County, FL</h2>
        <p>
          TPO (thermoplastic polyolefin) is one of the most common choices for properly designed low-slope roofs in Florida, and for good reason:
        </p>
        <ul>
          <li><strong>Reflective surface</strong> — the light-colored membrane reflects sunlight instead of absorbing it, which helps reduce heat buildup over Florida rooms and additions.</li>
          <li><strong>Heat resistance</strong> — TPO is formulated to handle sustained high temperatures and strong UV exposure.</li>
          <li><strong>Welded seams</strong> — seams are heat-welded rather than glued, forming a continuous bond across the roof surface.</li>
          <li><strong>Low-slope suitability</strong> — designed from the ground up for roofs where water drains slowly.</li>
        </ul>
        <p>
          For property owners with larger buildings, we also provide <Link to="/commercial-roofing">commercial roofing</Link> services including larger-scale TPO and low-slope systems.
        </p>

        <h2>Ponding Water &amp; Flat Roof Drainage</h2>
        <p>
          Drainage is the single most important factor in how long a low-slope roof lasts. Water that sits on a roof for extended periods accelerates membrane deterioration, finds its way into seams and flashings, and adds weight the roof was never meant to carry.
        </p>
        <p>
          Ponding usually comes from low areas in the roof surface, settled decking, or drainage points that are too few, too high, or blocked. When we evaluate a flat roof, drainage is one of the first things we look at. Where appropriate, tapered insulation may be used to build slope into the system and direct water toward roof drains or drainage points — though it isn't required on every roof, and we'll tell you honestly whether your roof needs it.
        </p>

        <h2>Common Flat Roof Problems We Repair</h2>
        <ul>
          <li><strong>Ponding water</strong> that won't drain and keeps returning to the same low areas</li>
          <li><strong>Failed seams</strong> where membrane sections have separated</li>
          <li><strong>Flashing leaks</strong> at walls, edges, and transitions</li>
          <li><strong>Membrane punctures</strong> from foot traffic, debris, or falling branches</li>
          <li><strong>Cracking or splitting</strong> in aging roofing material</li>
          <li><strong>Roof penetrations</strong> — failed seals around vents, A/C stands, and pipes</li>
          <li><strong>Tie-in leaks</strong> where the flat roof meets the pitched main roof</li>
          <li><strong>Aging or deteriorated roofing materials</strong> approaching end of life</li>
        </ul>

        <h2>Flat Roof Repair or Replacement?</h2>
        <RepairVsReplace />
        <p>
          A targeted <Link to="/roof-repair">roof repair</Link> may make sense when the roofing system is still serviceable and the source of the problem can be isolated — a single failed seam or flashing, for example. A full <Link to="/roof-replacement">roof replacement</Link> tends to make more sense when the membrane is extensively deteriorated, leaks keep coming back, drainage problems are significant, or the existing system is near the end of its useful life.
        </p>
        <p>
          You don't have to guess. A <Link to="/roof-inspection">roof inspection</Link> gives you photographs, a plain-language explanation, and a written estimate so you can decide on the facts.
        </p>

        <h2>Our Flat Roof Installation Process</h2>
        <ol>
          <li><strong>Inspect</strong> the existing roof and drainage.</li>
          <li><strong>Identify</strong> any damaged decking or substrate.</li>
          <li><strong>Determine</strong> the appropriate low-slope roofing system for the structure.</li>
          <li><strong>Prepare</strong> the roof and address drainage and detail work.</li>
          <li><strong>Install</strong> the roofing system, flashings, and penetrations.</li>
          <li><strong>Final inspection and cleanup</strong> — we walk the finished roof and leave the property clean.</li>
        </ol>

        <h2>Flat Roofing for Florida Rooms, Lanais &amp; Additions</h2>
        <p>
          In Citrus County, some of the hardest-working roofs on a home are the smallest ones. Florida rooms, lanais, screened porches, and additions are often built with a much lower slope than the main house — sometimes nearly flat. Standard shingles can't shed water properly at those pitches, which is why these areas so often develop leaks years before the main roof does.
        </p>
        <p>
          These spaces typically need a membrane or low-slope roofing system, correctly tied into the main roof so the transition doesn't become the next leak. Whether your lanai roof in <Link to="/roofing-company-inverness-fl">Inverness</Link> is dripping after every storm or your addition in <Link to="/roofing-company-lecanto-fl">Lecanto</Link> has water that never drains, we can inspect it and give you honest options. We handle flat and low-slope roofing across the county, including <Link to="/roofing-company-crystal-river-fl">Crystal River</Link> and surrounding communities.
        </p>
        <p>We also install <Link to="/mobile-home-roofing">TPO roof-overs for flat and low-slope mobile home roofs</Link>.</p>

        <h2>Why Choose Destination Roofing for Flat Roofing?</h2>
        <ul>
          <li>Local family-owned roofing contractor</li>
          <li>Licensed and insured — Florida Roofing License {BUSINESS.licenseNumber}</li>
          <li>Free roof inspections and estimates</li>
          <li>Residential and commercial roofing experience</li>
          <li>Repair and full replacement options — we'll tell you honestly which one your roof needs</li>
        </ul>

        <h2>Flat Roofing FAQs</h2>
        <div className="not-prose space-y-3 my-6">
          {FAQS.map((f) => (
            <details key={f.q} className="border border-border rounded-lg bg-card">
              <summary className="cursor-pointer font-semibold px-4 py-3">{f.q}</summary>
              <div className="px-4 pb-4 text-sm text-muted-foreground leading-relaxed">{f.a}</div>
            </details>
          ))}
        </div>

        <h2 id="request-inspection">Get a Free Flat Roof Inspection</h2>
        <p>
          If your Florida room, lanai, addition, or low-slope roof is leaking, ponding, or just getting old, we'll inspect it, show you photos of what we find, and give you a written estimate — no pressure, no scare tactics.
        </p>
        <div className="not-prose flex flex-col sm:flex-row gap-3 my-4">
          <Link to="/contact" className="btn-cta w-full sm:w-auto">
            <CalendarCheck className="size-5" /> Schedule My Free Inspection
          </Link>
          <a href={BUSINESS.phoneHref} className="inline-flex items-center justify-center gap-2 border-2 border-brand text-brand font-display font-bold uppercase tracking-wider rounded-md px-6 py-3 w-full sm:w-auto hover:bg-brand hover:text-brand-foreground transition-colors">
            <Phone className="size-5" /> Call {BUSINESS.phone}
          </a>
        </div>
      </ServicePageBody>
    </>
  );
}
